import React from 'react';
import {
  Form,
  Alert,
  Button,
  Col,
  Divider,
  Input,
  Row,
  Select,
  Spin,
  message,
  Checkbox,
} from 'antd';
import isEmpty from 'lodash/isEmpty';
import { getMeta, getAppMeta } from '@api/filters';
import { isAppModule, NO_COMPARE_MODE, COMPARE_PATH_TEST_STR } from '@utils/constants';
import { validateNewFilterBar } from '@components/compare-drawer/utils/checks';
import featureNames from '@utils/featureToggle/constants';
import { getRulesFromFilter, getFilterFromRules, getValidFilterDimensionMeta } from '@utils';
import styles from './NewFilterPanel.less';
import containerStyles from '../FilterContainer.less';
import FilterRuleGroup from './FilterRuleGroup';
import { TOOLTIP_FOR_HIDDEN } from '@src/components/filter/components/Management/FilterPanel/FilterBuilderPanel/constants';

export default class NewFilterPanel extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
    };
  }

  async componentDidMount() {
    const { categories, updateMeta } = this.props;
    const loading = isEmpty(categories);
    this.setState({
      loading,
    });
    if (loading) {
      try {
        // TODO: Now split app / video&ads into seperate func since video&ads may go to v2 api based FF control
        // After app filter APIs is migrated into new filter service then the following code can simplified into one func.
        const metaAsyncFunc = isAppModule ? getAppMeta : getMeta;
        const resp = await metaAsyncFunc();
        const { dimensions = [] } = resp;
        const filterCategories = resp.categories;
        updateMeta(filterCategories, dimensions);
        this.setState({
          loading: false,
        });
      } catch (err) {
        message.error(err.message);
      }
    }
  }

  render() {
    let elt;
    const { loading } = this.state;
    if (loading) {
      elt = <Spin />;
    } else {
      const {
        category,
        categories,
        dimensions: dimensionMeta,
        allDimensions,
        dataScope,
        rule,
      } = this.props;

      const validDimensionMeta =
        isAppModule && dataScope
          ? getValidFilterDimensionMeta(dimensionMeta, allDimensions)
          : dimensionMeta;

      const subcategories = categories[category];
      elt = (
        <>
          <Form layout="vertical" className={styles.newFilterPanel}>
            {this.renderName()}
            <Form.Item label="Rules" required>
              <FilterRuleGroup dimensions={validDimensionMeta} rule={rule} />
            </Form.Item>

            <Row gutter={16}>
              {this.renderCategoryDropdown()}
              {this.renderSubcategoryDropdown(subcategories)}
            </Row>
            <Row gutter={16}>{this.renderContentFilterCheckbox()}</Row>
          </Form>
          <Divider style={{ margin: '0 0 1rem 0' }} />
          {this.renderButtons()}
        </>
      );
    }
    return elt;
  }

  // Only ECO user can edit hidden status
  allowUpdateHidden = () => isAppModule;

  validateCompareFilters = () => {
    const { compareDrawer, savedFilters } = this.props;
    const validation = validateNewFilterBar(compareDrawer.filterBars, savedFilters);
    if (!validation.status) {
      message.error(validation.msg);
    }
    return validation.status;
  };

  onApply = () => {
    const {
      type,
      fid,
      applyFilter,
      updateFilter,
      compareMode,
      router,
      modal,
      toggleModal,
      resetDrillDown,
      pushDrilldownFilter,
      addSavedFilters,
      dashboardId,
      changeFilterContainerVisibility,
      nocDrawerFiltersMode,
      selectedFilters,
    } = this.props;
    const filterData = this.createFilterData();
    const rules = getRulesFromFilter(filterData.rule, true);
    const tmpFilter = getFilterFromRules(rules, true);
    if (!filterData.name) {
      filterData.name = tmpFilter.name;
    }

    if (
      compareMode === NO_COMPARE_MODE &&
      router.location.pathname.includes(COMPARE_PATH_TEST_STR)
    ) {
      if (this.validateCompareFilters()) {
        addSavedFilters([filterData], dashboardId);
        changeFilterContainerVisibility(false);
      }
      return;
    }

    const isAppliedFilter =
      Array.isArray(selectedFilters) && selectedFilters.some((it) => it.fid === fid);
    const skipDrilldown = isAppModule && type === 'edit' && !isAppliedFilter;

    if (!skipDrilldown) {
      resetDrillDown();
      pushDrilldownFilter(tmpFilter.name, tmpFilter.rule);
    }

    if (type === 'create') {
      applyFilter(filterData);
    } else {
      updateFilter(fid, filterData);
    }
  };

  onSave = () => {
    const {
      type,
      fid,
      createAndApplyFilter,
      updateAndApplyFilter,
      router,
      modal,
      toggleModal,
      compareMode,
      addSavedFilters,
      dashboardId,
      createFilter,
      changeFilterContainerVisibility,
      resetDrillDown,
      pushDrilldownFilter,
      nocDrawerFiltersMode,
    } = this.props;
    const filterData = this.createFilterData();
    if (
      compareMode === NO_COMPARE_MODE &&
      router.location.pathname.includes(COMPARE_PATH_TEST_STR)
    ) {
      if (this.validateCompareFilters()) {
        addSavedFilters([filterData], dashboardId);
        createFilter(filterData);
        changeFilterContainerVisibility(false);
      }
    }
    resetDrillDown();
    pushDrilldownFilter(filterData.name, filterData.rule);
    if (type === 'create') {
      createAndApplyFilter(filterData);
    } else {
      updateAndApplyFilter(fid, filterData);
    }
  };

  onUpdateName = (e) => {
    const { updateName } = this.props;
    updateName(e.target.value);
  };

  onUpdateCategory = (v) => {
    const { updateCategory, categories } = this.props;
    updateCategory(v, categories);
  };

  createFilterData() {
    const { rule, name, category, subcategory, isContent, hidden } = this.props;
    const filterData = {
      category,
      name,
      rule,
      subcategory,
      is_content: isContent,
    };
    if (this.allowUpdateHidden() && typeof hidden === 'boolean') {
      Object.assign(filterData, {
        isHiddenFromCustomer: hidden,
      });
    }
    return filterData;
  }

  isInvalidXMLString = (string) => {
    let match = false;

    // Regex for all not supported characters, currently we are skipping the discouraged charaters.
    const regex = new RegExp(
      // eslint-disable-next-line no-control-regex
      /((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/g,
    );
    match = regex.test(string);

    return match;
  };

  validateXMLRule = (name, rule) => {
    if (this.isInvalidXMLString(name)) {
      return {
        isXmlRuleInvalid: true,
        xmlErrorMsg: `Filter name '${name}' has invalid characters which are not supported.`,
      };
    }
    for (let i = 0; i < rule.length; i += 1) {
      const andRule = rule[i];
      for (let j = 0; j < andRule.length; j += 1) {
        const orRule = andRule[j];
        const { field, value } = orRule;
        if (field === 'OBJECT_NAME' && this.isInvalidXMLString(value)) {
          return {
            isXmlRuleInvalid: true,
            xmlErrorMsg: `Filter rule value '${value}' has invalid characters which are not supported.`,
          };
        }
      }
    }
    return { isXmlRuleInvalid: false, xmlErrorMsg: '' };
  };

  validation = (name, rule) => {
    const { features } = this.props;
    if (!name) {
      return false;
    }
    for (let i = 0; i < rule.length; i += 1) {
      const andRule = rule[i];
      for (let j = 0; j < andRule.length; j += 1) {
        const orRule = andRule[j];
        const { field, key, value } = orRule;
        if (value === '') {
          return false;
        }
        if (field === 'TAG' && (key === '' || value === '')) {
          return false;
        }
      }
    }
    return features[featureNames.CREATE_FILTER];
  };

  renderName = () => {
    const { name } = this.props;

    return (
      <Row gutter={16}>
        <Col span={15}>
          <Form.Item label="Filter Name" required>
            <Input
              placeholder="Please enter filter name"
              value={name}
              onChange={this.onUpdateName}
            />
          </Form.Item>
        </Col>
        {this.renderHiddenCheckbox()}
      </Row>
    );
  };

  renderCategoryDropdown = () => {
    const { categories, category } = this.props;
    return (
      <Col sm={6}>
        <Form.Item label="Category" required>
          <Select value={category} onChange={this.onUpdateCategory}>
            {Object.keys(categories)
              .sort()
              .map((cat) => (
                <Select.Option key={cat} value={cat}>
                  {cat}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
    );
  };

  // subcategories should have a default value for avoiding page crash
  renderSubcategoryDropdown = (subcategories = []) => {
    const { subcategory, updateSubcategory } = this.props;
    const isValidSubCategory = subcategories.includes(subcategory);

    return (
      <Col sm={6}>
        <Form.Item label="Subcategory" required>
          <Select
            key={subcategory}
            defaultValue={subcategory}
            onChange={updateSubcategory}
            disabled={!isValidSubCategory}
          >
            {subcategories.sort().map((sub) => (
              <Select.Option key={sub} value={sub}>
                {sub}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    );
  };

  renderContentFilterCheckbox = () => {
    const { updateContentFilter, features } = this.props;
    if (!features[featureNames.TOP_RISING_CONTENT]) {
      return null;
    }
    return (
      <>
        <Col sm={6}>
          <Form.Item
            label="Top Rising Content"
            tooltip="Enable this filter for the Top Rising Content module."
          />
        </Col>
        <Col sm={6}>
          <Checkbox defaultValue={false} onChange={updateContentFilter} />
        </Col>
      </>
    );
  };

  renderHiddenCheckbox = () => {
    const { hidden, type, updateFilterHidden, userProfile = {} } = this.props;
    // Only allow ECO hidden user to set hidden property
    if (this.allowUpdateHidden() && userProfile.hidden) {
      const finalHidden = type === 'create' && hidden === undefined ? userProfile.hidden : hidden;
      return (
        <Col sm={3}>
          <Form.Item label="Hide" tooltip={TOOLTIP_FOR_HIDDEN}>
            <Checkbox checked={finalHidden} onChange={updateFilterHidden} />
          </Form.Item>
        </Col>
      );
    }

    return null;
  };

  renderButtons = () => {
    const { onCancel, rule, name, type, saving } = this.props;

    const { isXmlRuleInvalid, xmlErrorMsg } = this.validateXMLRule(name, rule);
    const isUnderCreateMode = type === 'create';
    return (
      <div className={containerStyles.buttons}>
        {isXmlRuleInvalid && (
          <Alert message={xmlErrorMsg} type="error" className={styles.alertPanel} />
        )}
        <Button className={containerStyles.leftButton} onClick={onCancel}>
          Cancel
        </Button>
        <div className={containerStyles.rightBtn}>
          <Button
            type="primary"
            disabled={!this.validation('name', rule)}
            loading={!isUnderCreateMode && saving}
            onClick={this.onApply}
          >
            {isUnderCreateMode ? 'Apply' : 'Update'}
          </Button>
          <Button
            type="primary"
            onClick={this.onSave}
            disabled={!this.validation(name, rule) || isXmlRuleInvalid}
            loading={saving}
          >
            {isUnderCreateMode ? 'Save & Apply' : 'Update & Apply'}
          </Button>
        </div>
      </div>
    );
  };
}
